import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flame, Snowflake, Check, ArrowRight, Lock } from "lucide-react";
import api from "@/lib/api";

const parseDay = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };

function DayDot({ d, isToday, lang }) {
  const label = parseDay(d.day).toLocaleDateString(lang, { weekday: "narrow" });
  const tone = d.studied ? "bg-amber-500 border-amber-500 text-slate-950"
    : d.frozen ? "bg-sky-100 border-sky-300 text-sky-700"
      : isToday ? "border-dashed border-slate-400 text-slate-400" : "border-slate-200 text-slate-300";
  return (
    <div className="flex flex-col items-center gap-1.5" data-testid={`learning-streak-day-${d.day}`}>
      <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-transform hover:scale-110 ${tone}`}>
        {d.studied ? <Check size={14} strokeWidth={3} /> : d.frozen ? <Snowflake size={13} /> : null}
      </div>
      <span className={`text-[10px] font-semibold uppercase ${isToday ? "text-slate-900" : "text-slate-400"}`}>{label}</span>
    </div>
  );
}

function statusKey(s) {
  if (s.studied_today) return "doneToday";
  if (s.pending_freeze) return "freezeCovered";
  return s.current > 0 ? "keepGoing" : "start";
}

function LockedStreak() {
  const { t } = useTranslation();
  return (
    <div data-testid="learning-streak-locked" className="relative overflow-hidden bg-slate-950 text-white rounded-2xl p-6 sm:p-8 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 md:items-center">
      <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-amber-400/60">
        <Lock size={26} />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest text-amber-400 font-semibold">{t("learningStreak.lockedEyebrow")}</div>
        <div className="mt-1 font-display font-bold text-xl sm:text-2xl">{t("learningStreak.lockedTitle")}</div>
        <p className="mt-2 text-sm text-slate-300 max-w-xl">{t("learningStreak.lockedBody")}</p>
        <div className="mt-3 flex gap-2 opacity-60" aria-hidden>
          {[7, 30, 60, 90, 100].map((n) => (
            <span key={n} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1 text-[11px] font-bold"><Flame size={11} />{n}</span>
          ))}
        </div>
      </div>
      <Link to="/pricing?src=streak-locked" data-testid="learning-streak-upgrade-btn"
        className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-bold h-11 px-5 rounded-lg transition-colors">
        {t("learningStreak.lockedCta")} <ArrowRight size={14} />
      </Link>
    </div>
  );
}

export default function LearningStreakCard() {
  const { t, i18n } = useTranslation();
  const [s, setS] = useState(null);
  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    api.get("/streak/learning", { params: { tz } }).then((r) => setS(r.data)).catch(() => {});
  }, []);
  if (!s) return null;
  if (s.locked) return <LockedStreak />;
  const today = s.week[s.week.length - 1]?.day;
  const hot = s.current >= 3;
  return (
    <div data-testid="learning-streak-card" className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 md:items-center">
      <div className="flex items-center gap-4">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${hot ? "bg-amber-500 text-slate-950 shadow-[0_0_32px_rgba(245,158,11,.45)]" : "bg-slate-100 text-slate-500"}`}>
          <Flame size={30} className={hot ? "animate-pulse" : ""} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">{t("learningStreak.title")}</div>
          <div className="font-display font-black text-4xl text-slate-900 tabular-nums" data-testid="learning-streak-current">
            {t("learningStreak.days", { count: s.current })}
          </div>
          <div className="text-xs text-slate-500" data-testid="learning-streak-best">{t("learningStreak.best", { count: s.best })}</div>
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex gap-2.5 sm:gap-4">
          {s.week.map((d) => <DayDot key={d.day} d={d} isToday={d.day === today} lang={i18n.language || "en"} />)}
        </div>
        <p className="mt-3 text-sm text-slate-600" data-testid="learning-streak-status">{t(`learningStreak.status.${statusKey(s)}`)}</p>
      </div>
      <div className="flex flex-col items-start md:items-end gap-3">
        <span data-testid="learning-streak-freeze"
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider border ${s.freeze_available ? "bg-sky-50 border-sky-200 text-sky-800" : "bg-slate-50 border-slate-200 text-slate-500"}`}
          title={t("learningStreak.freezeHelp")}>
          <Snowflake size={12} /> {s.freeze_available ? t("learningStreak.freezeReady") : t("learningStreak.freezeUsed")}
        </span>
        {!s.studied_today && (
          <Link to="/courses" data-testid="learning-streak-cta"
            className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-700 text-white text-sm font-semibold h-10 px-4 rounded-lg transition-colors">
            {t("learningStreak.cta")} <ArrowRight size={14} />
          </Link>
        )}
      </div>
    </div>
  );
}
